import { ContentCardProps, ContentType } from "../components/ContentCard/contentCardTypes";
import { Level } from "../contexts/Level/LevelContextProvider";
import { levelToVariant } from "./colorUtils";

const LEVEL_NAME_REPLACE = "%{default_level_name}";
const LEVEL_DESC_REPLACE = "%{default_level_description}";

export const LESSON_TYPE_MAP: Record<string, ContentType> = {
  VideoLesson: "video",
  VideoWithCodingExercise: "video",
  CodingChallenge: "codingChallenge",
  CtfChallenge: "ctfChallenge",
  CodingExerciseItem: "exercise",
  HandsOnLesson: "breakfix",
  HackingChallenge: "hackingChallenge",
  AppLab: "app_lab",
  LESSON_CONFIGURATION_TYPE_UNSPECIFIED: "video",
};

export function replaceSubstringIfExists(
  originalString: string,
  searchSubstring: string,
  replacement: string
): string {
  if (originalString.includes(searchSubstring)) {
    return originalString.replace(searchSubstring, replacement);
  }
  return originalString;
}

/**
 * Input shapes consumed by the card mappers below. These mappers have no in-repo
 * callers and the fields are not emitted by any Rails presenter, so the types are
 * derived from exactly the fields each mapper reads (typed to match the
 * `ContentCardProps` output and the `Level`/`levelToVariant` signatures).
 */
type RawCollection = {
  levelId: number;
  name: string;
  description?: string;
  collectionType: ContentType;
  collectionKey?: string;
  combinedDuration?: number;
  lessonCount?: number;
  handsOnLessonCount?: number;
  videoLessonCount?: number;
};

type RawLesson = {
  type: string;
  lessonId?: string;
  lessonKey?: string;
  name?: string;
  description?: string;
  duration?: string;
};

export const mapCollectionToCard = (
  collection: RawCollection,
  levelsDetails?: Level[]
): ContentCardProps => {
  const associatedLevel = levelsDetails?.find(level => level.id === collection.levelId);
  const name = replaceSubstringIfExists(
    collection.name,
    LEVEL_NAME_REPLACE,
    associatedLevel?.name || "Level"
  );

  return {
    contentType: collection.collectionType,
    id: (collection.collectionKey as string) || "",
    lessonType: "path",
    name: name,
    title: name,
    description: replaceSubstringIfExists(
      collection.description || "",
      LEVEL_DESC_REPLACE,
      associatedLevel?.description || "Level description"
    ),
    difficulty: associatedLevel ? associatedLevel.name : undefined,
    cardSize: "small",
    variant: levelToVariant(collection.levelId),
    estimatedTimeToComplete: collection.combinedDuration,
    lessonsInEnrollment: collection.lessonCount || undefined,
    handsOnLessonCount: collection.handsOnLessonCount || undefined,
    videoLessonCount: collection.videoLessonCount || undefined,
  };
};

export const mapCollectionsToCards = (
  collections: RawCollection[],
  levelsDetails?: Level[]
): ContentCardProps[] => {
  return collections.map(collection => mapCollectionToCard(collection, levelsDetails));
};

export const mapLessonToCard = (lesson: RawLesson): ContentCardProps => {
  const contentType = LESSON_TYPE_MAP[lesson.type] || "video";

  return {
    contentType,
    lessonType: contentType,
    id: (lesson.lessonId as string) || "",
    lessonKey: lesson.lessonKey || "",
    title: lesson.name || "",
    description: lesson.description || "",
    estimatedTime: lesson.duration || undefined,
  };
};

export const mapLessonsToCards = (lessons: RawLesson[]): ContentCardProps[] => {
  return lessons.map(mapLessonToCard);
};
